// Geo-routing + click tracking for affiliate URLs
// Detects visitor country client-side (timezone heuristic + Cloudflare header fallback)
// then rewrites Amazon links to the regional storefront so commissions route correctly.

const COUNTRY_FROM_TZ = {
  // Asia/India
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  // Asia/SEA
  'Asia/Singapore': 'SG', 'Asia/Bangkok': 'TH', 'Asia/Manila': 'PH', 'Asia/Jakarta': 'ID', 'Asia/Ho_Chi_Minh': 'VN', 'Asia/Kuala_Lumpur': 'MY',
  // Asia/Other
  'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR', 'Asia/Hong_Kong': 'HK', 'Asia/Shanghai': 'CN', 'Asia/Dubai': 'AE',
  // Europe
  'Europe/London': 'UK', 'Europe/Dublin': 'IE',
  'Europe/Berlin': 'DE', 'Europe/Paris': 'FR', 'Europe/Madrid': 'ES', 'Europe/Rome': 'IT', 'Europe/Amsterdam': 'NL',
  'Europe/Stockholm': 'SE', 'Europe/Oslo': 'NO', 'Europe/Helsinki': 'FI', 'Europe/Warsaw': 'PL', 'Europe/Vienna': 'AT',
  // Americas
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US',
  'America/Phoenix': 'US', 'America/Anchorage': 'US', 'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
  'America/Mexico_City': 'MX', 'America/Sao_Paulo': 'BR',
  // Oceania
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Perth': 'AU', 'Pacific/Auckland': 'NZ',
};

const AMAZON_DOMAIN = {
  IN: 'amazon.in', US: 'amazon.com', CA: 'amazon.ca', MX: 'amazon.com.mx',
  UK: 'amazon.co.uk', DE: 'amazon.de', FR: 'amazon.fr', ES: 'amazon.es', IT: 'amazon.it', NL: 'amazon.nl',
  AU: 'amazon.com.au', JP: 'amazon.co.jp', AE: 'amazon.ae', SG: 'amazon.sg', BR: 'amazon.com.br',
};

// Affiliate tags per region — set these once you have your IDs (see ROADMAP / affiliate-playbook.md).
// Empty string = no tag appended (still routes geographically).
export const AMAZON_TAGS = {
  IN: 'gamesnfans-21',
  US: 'gamesnfans-20',
  UK: 'gamesnfans0d-21',
  DE: '', FR: '', ES: '', IT: '', NL: '',
  AU: '', CA: '', JP: '',
};

let detectedCountry = null;

export function detectCountry() {
  if (detectedCountry) return detectedCountry;
  if (typeof window === 'undefined') return 'US';

  // 1) Cloudflare / Vercel edge header injected via document.querySelector('meta[name="x-country"]')
  const metaCountry = document.querySelector('meta[name="x-country"]')?.content?.toUpperCase();
  if (metaCountry && AMAZON_DOMAIN[metaCountry]) {
    detectedCountry = metaCountry;
    return detectedCountry;
  }

  // 2) Timezone heuristic
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (COUNTRY_FROM_TZ[tz]) {
      detectedCountry = COUNTRY_FROM_TZ[tz];
      return detectedCountry;
    }
    // Coarse fallback by region prefix
    if (tz.startsWith('Asia/')) detectedCountry = 'IN';
    else if (tz.startsWith('Europe/')) detectedCountry = 'UK';
    else if (tz.startsWith('America/')) detectedCountry = 'US';
    else if (tz.startsWith('Australia/') || tz.startsWith('Pacific/')) detectedCountry = 'AU';
    else detectedCountry = 'US';
  } catch {
    detectedCountry = 'US';
  }
  return detectedCountry;
}

// Rewrite an Amazon affiliate URL to the visitor's regional storefront.
// Non-Amazon URLs pass through unchanged.
export function localizeAffiliate(url, countryOverride) {
  if (!url || typeof url !== 'string') return url;
  const country = countryOverride || detectCountry();

  try {
    const u = new URL(url, 'https://x.example/');
    const host = u.hostname.toLowerCase();
    // Only rewrite amazon.* domains
    const isAmazon = /(^|\.)amazon\.[a-z.]+$/.test(host);
    if (!isAmazon) return url;

    const targetDomain = AMAZON_DOMAIN[country] || AMAZON_DOMAIN.US;
    u.hostname = targetDomain;

    // Append affiliate tag if we have one
    const tag = AMAZON_TAGS[country];
    if (tag) u.searchParams.set('tag', tag);

    return u.toString();
  } catch {
    return url;
  }
}

// Fire Google Analytics / GA4 outbound event before navigation
export function trackOutboundClick({ retailer, product_id, product_name, page, country }) {
  if (typeof window === 'undefined') return;
  const payload = {
    event_category: 'outbound',
    event_label: retailer || 'unknown',
    transport_type: 'beacon',
    product_id: product_id || '',
    product_name: product_name || '',
    page: page || (typeof window !== 'undefined' ? window.location.pathname : ''),
    country: country || detectCountry(),
  };
  // GA4 (gtag.js)
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'outbound_click', payload);
  }
  // Plausible / generic dataLayer fallback
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: 'outbound_click', ...payload });
  }
}

export function getCountryName(code) {
  return ({
    IN: 'India', US: 'United States', UK: 'United Kingdom', CA: 'Canada', AU: 'Australia',
    DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy', NL: 'Netherlands',
    JP: 'Japan', AE: 'UAE', SG: 'Singapore', BR: 'Brazil', MX: 'Mexico', NZ: 'New Zealand',
  }[code] || code);
}
